/* eslint no-new: 0 */
'use es6';

import quickFetch from 'quick-fetch';

// I18n Configuration
// Keep these imports at top of file so that I18n data is defined
import createStandardI18nProvider from 'I18n/init/providers/createStandardI18nProvider';
import createAllI18nProvider from 'I18n/init/providers/createAllI18nProvider';
import { initAppTranslations } from 'I18n/init/setup/AppInit';
import registerAllData from 'I18n/init/register/lazy/registerAllData';
import lang from 'i2l?query=sporks!../lang/en.lyaml';
import sharedLegalStringsLang from 'i2l?mode=sync!SharedLegalStrings/lang/en.lyaml';
import 'I18n/init/data/baseLocales';
import 'I18n/init/data/publicLocales';
import 'I18n/init/data/allTimezones';
import 'I18n/init/data/currency';
import { renderRoot } from 'quartz-core/client';
import { List, Map as ImmutableMap } from 'immutable';
import PortalIdParser from 'PortalIdParser';
import userInfo from 'hub-http/userInfo';
import { RhumbProvider } from 'react-rhumb';
import rhumbConfig from '../rhumb-config.yaml';
import historyAdapter from 'react-rhumb/historyAdapterV4';
import { Application as ApplicationComponent } from 'foundations-components/components/Application';
import { DataFetchingClient, DataFetchingClientProvider } from 'data-fetching-client';
import { createStore, applyMiddleware, compose } from 'redux';
import thunk from 'redux-thunk';
import { Provider } from 'react-redux';
import { Router, Route, Switch, createBrowserHistory } from 'quartz-routing/react-router';
import EmailConfirmBarPrebuilt from 'email-confirm-ui/prebuilts/HubHttpPrebuilt';
import Raven from 'raven-js';
import TrackingMiddleware from './redux/middleware/TrackingMiddleware';
import NotificationMiddleware from 'ReduxMessenger/middleware/NotificationMiddleware';
import AdminContainer from 'MeetingsAdmin/components/AdminContainer';
import CalendarIndexContainer from 'MeetingsAdmin/components/CalendarIndexContainer';
import LoaderPage from './components/LoaderPage';
import reducers from './redux/reducers/root';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import MeetingsLink from 'MeetingsBase/models/MeetingsLink';
import { ravenDataCallback } from 'MeetingsLib/utils/errorUtils';
import ModalsContainer from './components/ModalsContainer';
import DefaultLinkRedirect from './components/DefaultLinkRedirect';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LoadableCreateMeetingsLinkContainer = Loadable({
  loader: () => import( /* webpackChunkName: "create-edit-setup" */'./components/CreateMeetingsLinkContainer'),
  LoadingComponent: LoaderPage,
  ErrorComponent: LoaderPage
});
const LoadableEditMeetingsLinkContainer = Loadable({
  loader: () => import( /* webpackChunkName: "create-edit-setup" */'./components/EditMeetingsLinkContainer'),
  LoadingComponent: LoaderPage,
  ErrorComponent: LoaderPage
});
const client = new DataFetchingClient();
const Routes = () => /*#__PURE__*/_jsxs(Switch, {
  children: [/*#__PURE__*/_jsx(Route, {
    path: "/meetings/:portalId"
  }), /*#__PURE__*/_jsx(AdminContainer, {
    children: /*#__PURE__*/_jsxs(Switch, {
      children: [/*#__PURE__*/_jsx(Route, {
        exact: true,
        path: "/link/default",
        component: DefaultLinkRedirect
      }), /*#__PURE__*/_jsx(Route, {
        exact: true,
        path: "/link/new",
        component: LoadableCreateMeetingsLinkContainer
      }), /*#__PURE__*/_jsx(Route, {
        exact: true,
        path: "/link/:linkId",
        component: LoadableEditMeetingsLinkContainer
      }), /*#__PURE__*/_jsx(Route, {
        exact: true,
        path: "/personal/:linkId",
        component: LoadableEditMeetingsLinkContainer
      }), /*#__PURE__*/_jsx(Route, {
        path: "/link/:linkId/:tab",
        component: LoadableEditMeetingsLinkContainer
      }), /*#__PURE__*/_jsx(Route, {
        exact: true,
        path: "/",
        component: CalendarIndexContainer
      })]
    })
  })]
});
export default class Application {
  start() {
    this.init().then(() => {
      this.actualStart();
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }

  // Performs all the initialization needed before the main app code can run/render.
  init() {
    PortalIdParser.get();
    Raven.setDataCallback(ravenDataCallback);

    // Kick off the auth process
    const authenticate = this.auth();
    return authenticate.then(authConfig => {
      this.authConfig = authConfig;
      const I18nProvider = createStandardI18nProvider();
      const AllI18nProvider = createAllI18nProvider();
      const langAvailable = initAppTranslations([I18nProvider.register(lang), registerAllData(I18nProvider), AllI18nProvider.register(sharedLegalStringsLang)]);
      I18nProvider.setLocale({
        locale: authConfig.user.locale,
        langEnabled: authConfig.user.lang_enabled,
        timezone: authConfig.portal.timezone
      });
      return langAvailable;
    });
  }
  auth() {
    return userInfo().then(({
      portal,
      gates,
      user
    }) => {
      Raven.setUserContext({
        id: user.user_id,
        email: user.email,
        locale: user.locale
      });
      return {
        portal,
        gates,
        user
      };
    }).catch(error => {
      throw error;
    });
  }
  getEarlyRequestLinkData() {
    const earlyRequestState = quickFetch.getRequestStateByName('meetings-links');
    if (!earlyRequestState || earlyRequestState.errored || !earlyRequestState.finished) {
      return undefined;
    }
    return List(earlyRequestState.result).map(ml => MeetingsLink.from(ml));
  }
  actualStart() {
    new EmailConfirmBarPrebuilt();
    const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
    const getBaseUrl = () => '/meetings';
    const getRootUrl = () => `${getBaseUrl()}/${PortalIdParser.get()}`;
    const history = createBrowserHistory({
      basename: getRootUrl()
    });
    const meetingsLinks = this.getEarlyRequestLinkData();
    const requestStatus = meetingsLinks ? ImmutableMap({
      MEETINGS_LINKS_FETCH: 'SUCCEEDED'
    }) : undefined;
    this.store = createStore(reducers, {
      auth: this.authConfig,
      meetingsLinks,
      requestStatus
    }, composeEnhancers(applyMiddleware(thunk, TrackingMiddleware, NotificationMiddleware)));
    renderRoot( /*#__PURE__*/_jsx(ApplicationComponent, {
      name: "MeetingsAdmin",
      children: /*#__PURE__*/_jsx(RhumbProvider, {
        history: historyAdapter(history),
        config: rhumbConfig,
        children: /*#__PURE__*/_jsx(DataFetchingClientProvider, {
          client: client,
          children: /*#__PURE__*/_jsxs(Provider, {
            store: this.store,
            children: [/*#__PURE__*/_jsx(ModalsContainer, {}), /*#__PURE__*/_jsx(Router, {
              history: history,
              children: /*#__PURE__*/_jsx(Routes, {})
            })]
          })
        })
      })
    }));
  }
}